/**
 * The upload queue, replacing agent/internal/upload/queue.go.
 *
 * It has changed character, and the difference matters. The Go queue held only
 * a folder path: Google Drive for Desktop moved the bytes and the job existed
 * just to ask raia-api to find and share the folder afterwards. With no sync
 * client, this queue holds THE PHOTOS THEMSELVES, in IndexedDB, until raia-api
 * has them.
 *
 * That makes durability the whole point rather than a nicety. An operator who
 * closes the tab with three sessions still queued must find them uploading when
 * they reopen it, so:
 *
 *  - every job is written before the first byte is sent, and updated after each
 *    file, so a retry resumes rather than restarting a 40MB session;
 *  - the photo blobs are stored beside the job and only deleted once each file
 *    is up;
 *  - `navigator.storage.persist()` is requested at startup, because "best
 *    effort" storage may be evicted and these are a paying customer's photos.
 *
 * What is NOT here any more is the "not synced yet" wait. The Go queue's normal
 * first few passes failed with ErrNotSynced while Drive caught up; the folder
 * now exists the moment `createSession` returns, so a failure here is a real
 * failure — venue wifi, a wrong password, or the backend being down.
 *
 * Retry cadence is the agent's 30s, kept because it was tuned against a venue
 * connection and there is no reason to be cleverer while a guest is waiting.
 */

import { createSession, uploadFile, type RaiaAccount } from '@/app/lib/raia'
import { shortCodeFor } from '@/core/session'
import { BLOBS, QUEUE, del, get, getAll, put, requestPersistence } from './db'
import { raiaConfigured, type Settings } from './settings'

const RETRY_MS = 30_000

export interface QueuedFile {
  /** Name the photo takes in the guest's Drive folder. */
  name: string
  /** Key into the BLOBS store. Deleted once the file is up. */
  blobId: string
  uploaded: boolean
}

export interface QueueJob {
  sessionKey: string
  shortCode: string
  createdAt: number
  files: QueuedFile[]
  /** Set once the folder exists and is shared; skipped on a retry. */
  folderId: string
  folderLink: string
  attempts: number
  lastError: string
}

export type QueueEvent =
  | { type: 'upload.progress'; sessionKey: string; done: number; total: number }
  | { type: 'upload.done'; sessionKey: string; shareUrl: string }
  | { type: 'upload.failed'; sessionKey: string; error: string }

type Listener = (event: QueueEvent) => void

const listeners = new Set<Listener>()

export function onQueueEvent(listener: Listener): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function emit(event: QueueEvent): void {
  for (const listener of listeners) listener(event)
}

// ── job storage ──────────────────────────────────────────────────────────────

export function listJobs(): Promise<QueueJob[]> {
  return getAll<QueueJob>(QUEUE)
}

async function saveJob(job: QueueJob): Promise<void> {
  await put(QUEUE, job)
}

async function dropJob(job: QueueJob): Promise<void> {
  // Blobs first: a job row without its blobs is recoverable noise, but blobs
  // without a job row are bytes nothing will ever clean up.
  for (const file of job.files) {
    await del(BLOBS, file.blobId).catch(() => undefined)
  }
  await del(QUEUE, job.sessionKey)
}

// ── holds ────────────────────────────────────────────────────────────────────

/**
 * Sessions queued before all their files exist — the animation, still encoding.
 *
 * The job used to be written only AFTER the encode, and that minute between the
 * print and the queue lived in this tab's memory alone. Session 1789377735564
 * (2026-09-14) was printed, its guest scanned the QR, and it never reached
 * raia-api: the tab did not survive that minute. So the job is now written the
 * moment the sheet goes to print, and held here while the video catches up.
 *
 * Deliberately in memory, not on the job row. A hold that survived a reload
 * would wait forever for an encode that died with the old tab; lost with the
 * tab, the job simply uploads without animate.mp4 — a missing extra, not a
 * guest whose link never resolves. The expiry covers an encode that hangs.
 */
const HOLD_MS = 5 * 60_000
const held = new Map<string, number>()

function isHeld(sessionKey: string): boolean {
  const until = held.get(sessionKey)
  if (until === undefined) return false
  if (until > Date.now()) return true
  held.delete(sessionKey)
  return false
}

/**
 * Adds the held files to a job and lets it upload.
 *
 * Returns false when the files could not be attached — the hold had already
 * expired, so a drain may be uploading the job right now, and appending to a
 * row `process` is about to overwrite would drop the file and orphan its blob.
 * The caller still has its local copy; the session itself is unaffected.
 */
export async function release(
  sessionKey: string,
  files: { name: string; blob: Blob }[] = [],
): Promise<boolean> {
  if (!isHeld(sessionKey)) {
    wake()
    return files.length === 0
  }

  // Extend rather than trust the remaining time: the writes below await, and a
  // hold expiring halfway through them is exactly the race this avoids.
  held.set(sessionKey, Date.now() + HOLD_MS)

  try {
    const job = await get<QueueJob>(QUEUE, sessionKey)
    if (!job) return false

    for (const file of files) {
      const blobId = `${sessionKey}/${job.files.length}/${file.name}`
      await put(BLOBS, file.blob, blobId)
      job.files.push({ name: file.name, blobId, uploaded: false })
    }
    await saveJob(job)
    return true
  } finally {
    // Only now: the row has to carry the new files before a drain may read it.
    held.delete(sessionKey)
    wake()
  }
}

// ── unfinished work ──────────────────────────────────────────────────────────

let filing = 0
let jobCount = 0

/**
 * Marks post-print work that has not finished filing its session, so closing
 * the tab in the middle of it is warned about (see `startQueue`).
 */
export async function whileFiling<T>(work: () => Promise<T>): Promise<T> {
  filing += 1
  try {
    return await work()
  } finally {
    filing -= 1
  }
}

/** True while closing this tab would leave a guest's link unresolved. */
export function hasUnfinishedWork(): boolean {
  return filing > 0 || jobCount > 0
}

/**
 * Queues one finished session.
 *
 * Called from the print step, after the sheet has gone to the browser's print
 * dialog — never before. Printing must not wait on any of this.
 *
 * `hold` queues it without letting it upload until `release`, for a session
 * whose animation is still encoding.
 */
export async function enqueue(
  sessionKey: string,
  files: { name: string; blob: Blob }[],
  options: { hold?: boolean } = {},
): Promise<void> {
  // Before the row exists, so no drain can ever see it unheld.
  if (options.hold) held.set(sessionKey, Date.now() + HOLD_MS)

  const stored: QueuedFile[] = []

  for (const [index, file] of files.entries()) {
    const blobId = `${sessionKey}/${index}/${file.name}`
    await put(BLOBS, file.blob, blobId)
    stored.push({ name: file.name, blobId, uploaded: false })
  }

  await saveJob({
    sessionKey,
    shortCode: shortCodeFor(sessionKey),
    createdAt: Date.now(),
    files: stored,
    folderId: '',
    folderLink: '',
    attempts: 0,
    lastError: '',
  })
  jobCount += 1

  wake()
}

/** Total bytes still waiting, so Settings can show a queue that is not draining. */
export async function queuedBytes(): Promise<number> {
  const jobs = await listJobs()
  let total = 0
  for (const job of jobs) {
    for (const file of job.files) {
      if (file.uploaded) continue
      const blob = await get<Blob>(BLOBS, file.blobId)
      total += blob?.size ?? 0
    }
  }
  return total
}

// ── the worker ───────────────────────────────────────────────────────────────

let timer: ReturnType<typeof setTimeout> | undefined
let running = false
/** A wake() that arrived mid-drain; honoured when it ends rather than in 30s. */
let again = false
let account: (() => RaiaAccount | null) | null = null

/**
 * The agent drained uploads as a service that outlived the browser. This tab is
 * the queue now, so closing it with a session still going up is the one gesture
 * that loses a guest's link — worth the browser's own "Leave site?" prompt. It
 * asks only while there is something to lose; an idle console closes silently.
 */
function guardUnload(event: BeforeUnloadEvent): void {
  if (!hasUnfinishedWork()) return
  event.preventDefault()
  // Older Chromium ignores preventDefault() without this.
  event.returnValue = ''
}

/**
 * Starts draining. Idempotent — AppShell restarts it whenever settings change,
 * and a second call must not leave two timers racing over the same job rows.
 */
export function startQueue(settings: () => Settings | null): () => void {
  account = () => {
    const current = settings()
    if (!current || !raiaConfigured(current)) return null
    return { baseUrl: current.apiBaseUrl, email: current.apiEmail, password: current.apiPassword }
  }

  void requestPersistence()
  wake()

  // Anything queued while the venue was offline should go the moment it is not.
  window.addEventListener('online', wake)
  window.addEventListener('beforeunload', guardUnload)

  return () => {
    window.removeEventListener('online', wake)
    window.removeEventListener('beforeunload', guardUnload)
    if (timer) clearTimeout(timer)
    timer = undefined
    account = null
  }
}

function schedule(): void {
  if (timer) clearTimeout(timer)
  timer = setTimeout(() => {
    timer = undefined
    void drain()
  }, RETRY_MS)
}

export function wake(): void {
  if (timer) clearTimeout(timer)
  timer = undefined
  void drain()
}

async function drain(): Promise<void> {
  if (running) {
    again = true
    return
  }
  running = true

  try {
    const all = await listJobs()
    jobCount = all.length
    const jobs = all
      .filter((job) => !isHeld(job.sessionKey))
      .sort((a, b) => a.createdAt - b.createdAt)
    if (jobs.length === 0) return

    const credentials = account?.() ?? null
    // Not configured yet: the jobs stay put, exactly as the Go queue left them
    // on disk. This is the "Akun Raia belum diatur" case and is not an error.
    if (!credentials) return

    for (const job of jobs) {
      try {
        await process(credentials, job)
        await dropJob(job)
        emit({ type: 'upload.done', sessionKey: job.sessionKey, shareUrl: job.folderLink })
      } catch (error) {
        job.attempts += 1
        job.lastError = error instanceof Error ? error.message : String(error)
        await saveJob(job)
        emit({ type: 'upload.failed', sessionKey: job.sessionKey, error: job.lastError })
        // Move on rather than blocking the whole queue behind one bad session —
        // the guest behind it is waiting on their own link.
      }
    }
  } finally {
    // Re-arm only while there is something left; an empty queue should not hold
    // a timer open all evening. A listing that fails counts as "something left":
    // treating it as empty would stop the queue for good.
    const remaining = await listJobs().then(
      (left) => left.length,
      () => -1,
    )
    if (remaining >= 0) jobCount = remaining

    // The flag is cleared last so a wake() arriving during the listing cannot
    // start a second drain over the same job rows — it sets `again` instead.
    running = false
    if (again) {
      again = false
      void drain()
    } else if (remaining !== 0) {
      schedule()
    }
  }
}

async function process(credentials: RaiaAccount, job: QueueJob): Promise<void> {
  if (!job.folderId) {
    const created = await createSession(credentials, job.sessionKey, job.shortCode)
    job.folderId = created.folderId
    job.folderLink = created.folderLink
    // Persist before uploading: if the tab closes now, the retry must not
    // create a second folder for the same session.
    await saveJob(job)
  }

  const total = job.files.length
  let done = job.files.filter((file) => file.uploaded).length
  emit({ type: 'upload.progress', sessionKey: job.sessionKey, done, total })

  for (const file of job.files) {
    if (file.uploaded) continue

    const blob = await get<Blob>(BLOBS, file.blobId)
    if (!blob) {
      // The bytes are gone — evicted, or already deleted by a pass that died
      // between the upload and the save. Either way there is nothing left to
      // send, and blocking the session on it forever helps nobody.
      file.uploaded = true
      await saveJob(job)
      continue
    }

    await uploadFile(credentials, job.sessionKey, file.name, blob)

    file.uploaded = true
    done += 1
    // One save per file is what makes a 12MB photo on venue wifi worth sending
    // only once.
    await saveJob(job)
    await del(BLOBS, file.blobId).catch(() => undefined)
    emit({ type: 'upload.progress', sessionKey: job.sessionKey, done, total })
  }
}
